import { useState, useEffect } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Cloud, CloudOff, RefreshCw, ArrowRightLeft, ShieldCheck, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

// Founder-facing control for the one-time move to Amazon S3. Lives here (and not
// in a script) because the PRODUCTION database and AWS secrets only exist inside
// the production pod — this is the authenticated door into it.
export const ObjectStorageCard = () => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState('');
  const [progress, setProgress] = useState(null);
  const [result, setResult] = useState(null);

  const load = async () => {
    try {
      const res = await api.get('/api/admin/storage/status');
      setStatus(res.data);
    } catch {
      setStatus({ unavailable: true });
    }
  };
  useEffect(() => { load(); }, []);

  const run = async (mode) => {
    setBusy(mode);
    setResult(null);
    setProgress(null);
    try {
      const { data } = await api.post(`/api/admin/storage/migrate?mode=${mode}`);
      // Long copies run in the background — poll until the job reports done.
      let job = null;
      for (let i = 0; i < 600; i++) {
        await new Promise((r) => setTimeout(r, 1500));
        const res = await api.get(`/api/admin/storage/migrate/jobs/${data.job_id}`);
        job = res.data;
        setProgress({ processed: job.processed, total: job.total_keys });
        if (job.status !== 'running') break;
      }
      if (job?.status === 'failed') {
        toast.error(job.error || 'Migration failed');
      } else {
        setResult({ mode, ...job });
        toast.success(mode === 'apply' ? 'Files moved to S3' : `${mode.replace('_', ' ')} complete`);
      }
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(d?.message || (typeof d === 'string' ? d : 'Request failed'));
    }
    setBusy('');
    setProgress(null);
  };

  if (!status) return null;
  if (status.unavailable) return null;

  const onS3 = status.using_s3;
  const healthy = onS3 && status.bucket_reachable;

  return (
    <Card className="rounded-2xl border-slate-200" data-testid="object-storage-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          {onS3 ? <Cloud className="h-4 w-4 text-sky-600" /> : <CloudOff className="h-4 w-4 text-muted-foreground" />}
          Object storage
          <Badge variant="outline" className={healthy
            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
            : onS3 ? 'bg-rose-50 text-rose-700 border-rose-200' : 'bg-amber-50 text-amber-700 border-amber-200'}
            data-testid="object-storage-provider-badge">
            {onS3 ? (healthy ? 'Amazon S3 · live' : 'Amazon S3 · unreachable') : 'Emergent storage'}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          {[
            ['Bucket', status.bucket || 'not set'],
            ['Region', status.region || 'not set'],
            ['Files in this database', status.objects_referenced_in_db],
            ['Dual read', status.dual_read ? 'on (safe)' : 'off'],
          ].map(([k, v]) => (
            <div key={k} className="p-2 rounded-lg bg-slate-50 border">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{k}</p>
              <p className="font-medium truncate">{String(v)}</p>
            </div>
          ))}
        </div>

        {status.bucket_error && (
          <p className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-2" data-testid="object-storage-error">
            {status.bucket_error}
          </p>
        )}
        {!onS3 && (
          <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2">
            This environment still writes to Emergent storage. Add STORAGE_PROVIDER=s3 plus the
            AWS keys, bucket and region in the Secrets tab, re-publish, then run the migration below.
          </p>
        )}

        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" disabled={!!busy} onClick={() => run('dry_run')} data-testid="storage-dry-run-button">
            {busy === 'dry_run' ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
            Dry run
          </Button>
          <Button size="sm" disabled={!!busy || !onS3} onClick={() => run('apply')} data-testid="storage-migrate-button">
            {busy === 'apply' ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <ArrowRightLeft className="h-3.5 w-3.5 mr-1.5" />}
            Move files to S3
          </Button>
          <Button size="sm" variant="outline" disabled={!!busy || !onS3} onClick={() => run('verify')} data-testid="storage-verify-button">
            {busy === 'verify' ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5 mr-1.5" />}
            Verify
          </Button>
        </div>

        {progress && (
          <p className="text-xs text-muted-foreground" data-testid="storage-migration-progress">
            {progress.processed || 0}{progress.total ? ` of ${progress.total}` : ''} files processed…
          </p>
        )}

        {result && (
          <div className="text-xs bg-slate-900 text-slate-100 rounded-lg p-3 font-mono space-y-1" data-testid="storage-migration-result">
            <p>mode: {result.mode} · files referenced: {result.total_keys}</p>
            {result.inventory && <p>by folder: {Object.entries(result.inventory).map(([k, v]) => `${k}=${v}`).join('  ')}</p>}
            {result.tally && <p>result: {Object.entries(result.tally).map(([k, v]) => `${k}=${v}`).join('  ')} · {result.mb_touched} MB</p>}
            {result.mode === 'verify' && <p>present in S3: {result.present}/{result.total_keys} · missing: {result.missing}</p>}
            {(result.failures?.length > 0 || result.missing_keys?.length > 0) &&
              (result.failures || result.missing_keys).map((f, i) => <p key={i} className="text-rose-300">{f}</p>)}
          </div>
        )}

        <p className="text-[11px] text-muted-foreground">
          Safe to run more than once — files already in the bucket are skipped. Nothing is deleted
          from the old store, so this is reversible until dual read is switched off.
        </p>
      </CardContent>
    </Card>
  );
};
